import { useEffect, useState } from 'react';
import { graphql } from 'react-relay';

import { useSubscriptionInterop } from '@confluence/apollo-relay-interop/entry-points/useSubscriptionInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	useOptimisticCommentAgentSession,
	getOptimisticCommentAgentIdentity,
	type CommentAgentIdentity,
} from './optimisticCommentAgentSession.ts';
import { useSubmittedCommentAgentAction } from './submittedCommentAgentAction.ts';
import { AGENT_SESSION_SUBSCRIPTION_FAILED_EVENT } from './agentMentionConstants.ts';
import type { useCommentAgentSessionStatusSubscription as CommentAgentSessionStatusSubscriptionType } from './__generated__/useCommentAgentSessionStatusSubscription.graphql.ts';

/**
 * Live status subscription for a single comment's agent conversation.
 *
 * This is an *observe-only* channel: it streams session-status updates for the given
 * conversation without competing with the conversation's own message stream (owned by the
 * open CwR sidebar right after a mention) — which is what made a direct POST to the chat
 * `message/stream/sse` endpoint unreliable for freshly-mentioned agents.
 *
 * The union member's `__typename` drives our ConversationState:
 *   - ConvoAiTraceMessage                                  → WORKING
 *   - ConvoAiConversationMessage (state COMPLETED)         → hidden
 *   - ConvoAiConversationMessage (state FAILED)            → FAILED
 *   - ConvoAiConversationMessage (ASSISTANT, has actions)  → INPUT_REQUIRED
 *   - ConvoAiConversationMessage (ASSISTANT, no actions)   → ARTEFACT_READY
 *   - ConvoAiErrorMessage                                  → FAILED
 *   - ConvoAiEmptyConversation / other                     → ignored
 *
 * Modelled on jira-ai/agent-stack-unconnected's AgentSessionStatusSubscription. Defined inline
 * (not a separate module) per graphql-relay-compat/no-import-relay-graphql-operations.
 */
const CommentAgentSessionStatusSubscription = graphql`
	subscription useCommentAgentSessionStatusSubscription($cloudId: ID!, $conversationId: ID!) {
		convoai_onAgentSessionUpdate(cloudId: $cloudId, conversationId: $conversationId)
			@optIn(to: "ConvoAiAgentSessionUpdate") {
			__typename
			... on ConvoAiTraceMessage {
				messageTemplate
			}
			... on ConvoAiConversationMessage {
				role
				agentSessionState
				actions {
					key
				}
			}
			... on ConvoAiErrorMessage {
				errorMessageTemplate: messageTemplate
			}
		}
	}
`;

/**
 * The four conversation states surfaced for a comment's agent session.
 *
 * Modelled after the A2A task state spec:
 * - WORKING        — agent is actively processing
 * - ARTEFACT_READY — agent finished cleanly; no user action required
 * - INPUT_REQUIRED — user must resolve pending actions or a policy block
 * - FAILED         — the agent reported an error
 *
 * See: https://hello.atlassian.net/wiki/spaces/AIC1/pages/7123446755
 */
export type ConversationState = 'WORKING' | 'ARTEFACT_READY' | 'INPUT_REQUIRED' | 'FAILED';

export type CommentAgentSession = {
	/**
	 * The derived conversation state, or null when there is no conversation to track yet
	 * (the BE association is not available).
	 */
	state: ConversationState | 'THINKING' | null;
	/** Mention identity before CAP creates the session association. */
	agentIdentity?: CommentAgentIdentity;
	/** The conversationId this session is tracking. */
	conversationId: string | undefined;
};

// Snapshot states for which the agent's turn has genuinely ended, so there is nothing left to
// stream — we surface the snapshot directly and skip opening a live subscription (a perf win on
// page load, where most sessions are already settled).
//
// INPUT_REQUIRED is deliberately excluded: it is NOT terminal — it means the agent is paused
// waiting for the user. When the user replies (via the Rovo sidebar) the conversation resumes
// with fresh WORKING → ARTEFACT_READY messages, so the subscription must stay open to receive
// them. Treating it as terminal would freeze the badge on "Waiting for your input" for the
// rest of the session.
const TERMINAL_STATES = new Set<ConversationState>(['ARTEFACT_READY', 'FAILED']);

type StreamState = ConversationState | 'COMPLETED';

type SessionUpdateMessage =
	CommentAgentSessionStatusSubscriptionType['response']['convoai_onAgentSessionUpdate'];

/**
 * Maps a single `convoai_onAgentSessionUpdate` message to a stream state, or null when the
 * message carries no status signal (and should be ignored). COMPLETED is kept as an internal
 * sentinel so the public state can become null and remove the status row.
 *
 * Only explicit backend failure signals map to FAILED — either a failed agent session state or
 * a ConvoAiErrorMessage. Transport/connection problems are handled by the subscription layer
 * and never surface as a (false) chat failure here.
 */
function mapMessageToState(message: SessionUpdateMessage | null | undefined): StreamState | null {
	switch (message?.__typename) {
		case 'ConvoAiTraceMessage':
			return 'WORKING';
		case 'ConvoAiConversationMessage':
			if (message.agentSessionState === 'COMPLETED' || message.agentSessionState === 'FAILED') {
				return message.agentSessionState;
			}
			// Only assistant messages reflect non-terminal agent progress.
			if (message.role !== 'ASSISTANT') {
				return null;
			}
			// Pending actions mean the user has to respond; otherwise the result is ready.
			return message.actions && message.actions.length > 0 ? 'INPUT_REQUIRED' : 'ARTEFACT_READY';
		case 'ConvoAiErrorMessage':
			return 'FAILED';
		default:
			// ConvoAiEmptyConversation and any future/unknown member: no status change.
			return null;
	}
}

/**
 * Subscribes to live status for a single comment's agent conversation and returns the
 * current ConversationState.
 *
 * Uses the `convoai_onAgentSessionUpdate` Relay subscription — an observe-only channel that
 * does not compete with the conversation's own message stream (owned by the CwR sidebar),
 * which is what made a direct SSE POST unreliable for freshly-mentioned agents.
 *
 * @param commentId - The comment this session belongs to; keys optimistic startup feedback.
 * @param conversationId - The Rovo conversationId; null until the BE association exists.
 * @param initialState - Optional snapshot state from `content.allCommentAgentSessions`. When
 *                       it is already terminal we surface it directly and skip the subscription.
 * @returns `{ state, conversationId }` — THINKING during optimistic startup, then CAP status.
 */
export function useCommentAgentSession(
	commentId: string,
	conversationId: string | undefined,
	initialState?: ConversationState,
): CommentAgentSession {
	const { cloudId } = useSessionData();
	const isStarting = useOptimisticCommentAgentSession(
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ? commentId : '',
		!!conversationId || !!initialState,
	);

	// Live status observed from the subscription. null until a message arrives. We never set
	// this from a transport/connection error — only explicit backend failure signals map to
	// FAILED — so a transient blip is never mistaken for a failed chat.
	const [streamState, setStreamState] = useState<StreamState | null>(null);

	// Reset observed state when the conversation changes, so a comment never briefly shows a
	// previous conversation's status.
	useEffect(() => {
		setStreamState(null);
	}, [conversationId]);

	// Skip the subscription when there is nothing to observe, or when the backend snapshot is
	// already terminal (the agent finished in a prior session — show the snapshot directly).
	const snapshotIsTerminal = !!initialState && TERMINAL_STATES.has(initialState);
	// A delayed query must not close a subscription that has reported newer work.
	const skip =
		!cloudId ||
		!conversationId ||
		(snapshotIsTerminal &&
			(!expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ||
				streamState !== 'WORKING'));

	function recoverFromSubscriptionFailure() {
		if (
			skip ||
			streamState === 'COMPLETED' ||
			(streamState && TERMINAL_STATES.has(streamState)) ||
			!expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
		)
			return;
		// Drop stale live status until live updates resume. Preserve a terminal message even
		// when it arrived in the same React batch as the transport completion.
		setStreamState((current) =>
			current === 'COMPLETED' || (current && TERMINAL_STATES.has(current)) ? current : null,
		);
		window.dispatchEvent(new Event(AGENT_SESSION_SUBSCRIPTION_FAILED_EVENT));
	}

	const subscription = useSubscriptionInterop<
		CommentAgentSessionStatusSubscriptionType['response'],
		CommentAgentSessionStatusSubscriptionType['variables']
	>(CommentAgentSessionStatusSubscription, {
		skip,
		variables: { cloudId: cloudId ?? '', conversationId: conversationId ?? '' },
		interopApolloAggClient: true,
		onSubscriptionData: ({ subscriptionData }) => {
			const next = mapMessageToState(subscriptionData.data?.convoai_onAgentSessionUpdate);
			if (next) {
				setStreamState(next);
			}
		},
		...(expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			? { onSubscriptionComplete: recoverFromSubscriptionFailure }
			: {}),
	});

	useEffect(() => {
		// The subscription hook exposes errors as results, so recovery follows that external update.
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		if (subscription?.error) recoverFromSubscriptionFailure();
		// An unchanged error must not discard live messages received after recovery.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subscription?.error, conversationId]);

	// Prefer the live subscription status, fall back to the backend snapshot, and finally to
	// WORKING while a conversation exists but has not reported a status yet. A completed session
	// returns null so consumers remove its status row.
	const state: ConversationState | null =
		streamState === 'COMPLETED'
			? null
			: (streamState ?? initialState ?? (conversationId ? 'WORKING' : null));

	const submission = useSubmittedCommentAgentAction(
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			? (conversationId ?? '')
			: '',
	);
	// Keep submission progress or recovery visible until comments have been reconciled.
	const hasPendingSubmission = submission && submission.state.status !== 'resolved';
	return {
		state: hasPendingSubmission ? 'INPUT_REQUIRED' : (state ?? (isStarting ? 'THINKING' : null)),
		conversationId,
		agentIdentity: isStarting ? getOptimisticCommentAgentIdentity(commentId) : undefined,
	};
}
